/**
 * Front-end mirror of the canonical permission registry defined on the backend at
 * `Leo.Cars.Raptor.Core/Authorization/Permissions.cs`. This is the single source of truth for
 * permission key strings on the client: gating directives, route guards, and the application
 * context store reference these constants rather than raw string literals so the key set stays
 * aligned with the backend catalog.
 *
 * This file is generated by `tools/generate-permission-keys.mjs`; do not edit it by hand. Run
 * `npm run generate-permission-keys` (or the script directly) after any change to the backend
 * registry to regenerate it. A drift check compares the key set produced by {@link ALL_PERMISSIONS}
 * against the backend registry and the seed matrix; the two sets MUST remain equal.
 */

export const Permissions = {
    /** Appointment scheduling and check-in permissions. */
    Appointment: {
        View: 'appointment:view',
        CompleteCheckIn: 'appointment:completecheckin',
        Create: 'appointment:create',
        Delete: 'appointment:delete',
        Update: 'appointment:update',
        /** Persist a federation-sourced appointment via DoSync. Granted to the federation service principals that push appointment data into Raptor. */
        Sync: 'appointment:sync',
        /** Manage the tenant/branch pickup-option configuration offered at check-in (upsert and remove, tenant defaults and branch overrides). This is managed configuration rather than free-form appointment data, so it is granted only to the tenant-admin tier -- not to the store-manager or advisor tiers that hold the general appointment write permissions. */
        ManagePickupOptions: 'appointment:managepickupoptions',
        /** Manage the tenant/branch self check-in field configuration: which fields the customer surface shows, and which are required, read-only or format-validated. Granted to the tenant-admin tier only, for the same reason as ManagePickupOptions. */
        ManageSelfCheckInFields: 'appointment:manageselfcheckinfields',
    },
    /** Branch configuration permissions. */
    Branch: {
        View: 'branch:view',
        Update: 'branch:update',
        /** Create a branch. Admin/system-only; not granted to any catalog-governed role. */
        Create: 'branch:create',
        /** Delete a branch. Admin/system-only; not granted to any catalog-governed role. */
        Delete: 'branch:delete',
        /** Synchronise a branch. Admin/system-only; not granted to any catalog-governed role. */
        Sync: 'branch:sync',
        /** Change a branch's SMS sender number. Held apart from Update because the number is provisioned with the SMS provider. Admin/system-only; not granted to any catalog-governed role. */
        UpdateTextingPhoneNumber: 'branch:updatetextingphonenumber',
    },
    /** Customer record management permissions. */
    Customer: {
        View: 'customer:view',
        Create: 'customer:create',
        Delete: 'customer:delete',
        Sync: 'customer:sync',
        Update: 'customer:update',
        /** Send an outbound customer text message and view the two-way conversation thread. Granted to the advisor tier (tenantAdmin/storeManager/advisor) -- the same tier that already holds View -- because outbound customer messaging is a service-desk operation, not a technician or customer-surface capability. */
        Text: 'customer:text',
    },
    /** Inspection lifecycle permissions. */
    Inspection: {
        View: 'inspection:view',
        /** Gates visibility of the mobile DVI inspection-list surface: the mobile side-menu entry and the inspection-list route. Distinct from View, which continues to gate individual inspection detail, create, and edit routes. A holder sees the list surface; a non-holder loses the menu entry and is redirected away from the list route. This is a visibility gate only -- the backend list endpoint remains authoritative and is governed by the ordinary read-access rules -- so revoking it hides the list from a role without stripping their ability to work with inspections opened through other flows (e.g. the tech-work "My Work" landing page). */
        ListView: 'inspection:listview',
        Create: 'inspection:create',
        CreateCheckIn: 'inspection:createcheckin',
        Delete: 'inspection:delete',
        /** Gates the advisor pre-transfer review checkpoint: reviewing a completed DVI before it is federated and reopening it for correction. This key is the enforcement anchor for the opt-in advisor-review gate. It MUST NOT be granted to the Technician tier -- a technician who could both perform and clear their own review would defeat the checkpoint entirely (self-review-and-transfer) -- nor to the Customer tier, which has no authoring authority over the inspection lifecycle. */
        Review: 'inspection:review',
        ShareConsolidated: 'inspection:shareconsolidated',
        Transfer: 'inspection:transfer',
        Update: 'inspection:update',
        /** Gates the "see all inspections in the branch" visibility tier of the my-inspections list. A holder sees every inspection in the branch; a non-holder sees only inspections assigned to them (plus, for the technician-eligible pool, unassigned inspections). This is a visibility tier, not an operation gate: no controller requires it. It is enforced imperatively in the service layer (like ManageGlobal), because the scoping predicate is composed at query time rather than at a controller attribute. Granted to the tenant-admin and store-manager tiers, which previously saw all inspections by role. */
        ViewAll: 'inspection:viewall',
        /** Marks a role whose holders are eligible to be assigned an inspection as the technician. This is an assignment-eligibility marker, not an enforced operation gate: no controller requires it. It exists so the frontend technician-assignment dropdowns are populated from each user's effective permissions rather than a hard-coded role name, which makes the dropdowns custom-role aware (a tenant role copied from technician inherits this key). */
        AssignableAsTechnician: 'inspection:assignableastechnician',
        /** Marks a role whose holders are eligible to be assigned an inspection as the advisor. This is an assignment-eligibility marker, not an enforced operation gate: no controller requires it. It exists so the frontend advisor-assignment dropdowns are populated from each user's effective permissions rather than a hard-coded role name, which makes the dropdowns custom-role aware (a tenant role copied from advisor inherits this key). */
        AssignableAsAdvisor: 'inspection:assignableasadvisor',
    },
    /** Inspection template management permissions. */
    InspectionTemplate: {
        View: 'inspectiontemplate:view',
        Clone: 'inspectiontemplate:clone',
        Create: 'inspectiontemplate:create',
        Delete: 'inspectiontemplate:delete',
        Update: 'inspectiontemplate:update',
        /** Create, edit, delete, or clone a tenant-wide (global-scope) inspection template. Branch-scope templates are governed by the ordinary Create/Update/Delete/Clone keys; this key additionally gates the global-scope variants and is granted to tenantAdmin only. Enforced imperatively in the service layer (scope is only known once the entity or target scope is resolved), so it is not a controller-attribute gate. */
        ManageGlobal: 'inspectiontemplate:manageglobal',
    },
    /** Job (service line) management permissions. */
    Job: {
        View: 'job:view',
        Assign: 'job:assign',
        Create: 'job:create',
        Delete: 'job:delete',
        Update: 'job:update',
        UpdateStatus: 'job:updatestatus',
    },
    /** Job category management permissions. */
    JobCategory: {
        View: 'jobcategory:view',
        Create: 'jobcategory:create',
        Delete: 'jobcategory:delete',
        Update: 'jobcategory:update',
    },
    /** Quote management permissions. */
    Quote: {
        View: 'quote:view',
        Create: 'quote:create',
        ApplyJob: 'quote:applyjob',
        PriceOverride: 'quote:priceoverride',
        Sync: 'quote:sync',
        Update: 'quote:update',
        Delete: 'quote:delete',
    },
    /** Work order management permissions. */
    WorkOrder: {
        View: 'workorder:view',
        ApplyJob: 'workorder:applyjob',
        Update: 'workorder:update',
        /** Synchronise work orders from the source system. */
        Sync: 'workorder:sync',
        /** Mark a work order as complete. */
        Complete: 'workorder:complete',
        /** Update the quantities of parts on a work order. */
        UpdatePartQuantities: 'workorder:updatepartquantities',
    },
    /** Dispatch board permissions. */
    Dispatch: {
        View: 'dispatch:view',
        Create: 'dispatch:create',
        Update: 'dispatch:update',
    },
    /** Manufacturer catalog permissions. */
    Manufacturer: {
        View: 'manufacturer:view',
        Create: 'manufacturer:create',
        Delete: 'manufacturer:delete',
        Sync: 'manufacturer:sync',
        Update: 'manufacturer:update',
    },
    /** Add-on catalog permissions. */
    Addon: {
        Create: 'addon:create',
        Update: 'addon:update',
        Delete: 'addon:delete',
    },
    /** Part catalog permissions. */
    Part: {
        View: 'part:view',
        Create: 'part:create',
        /** Update an existing part in the catalog. Required (alongside Create) by the bulk Parts import definition so an import may both create new parts and update existing ones as part of a single all-or-nothing commit. */
        Update: 'part:update',
    },
    /** Inventory permissions. */
    Inventory: {
        View: 'inventory:view',
        Sync: 'inventory:sync',
    },
    /** Inventory list (stock-take) permissions. */
    InventoryList: {
        View: 'inventorylist:view',
        Approve: 'inventorylist:approve',
        Create: 'inventorylist:create',
        Delete: 'inventorylist:delete',
        Update: 'inventorylist:update',
        UpdateItemScannedQuantity: 'inventorylist:updateitemscannedquantity',
    },
    /** Counting document permissions. */
    CountingDocument: {
        View: 'countingdocument:view',
        Reset: 'countingdocument:reset',
        Update: 'countingdocument:update',
    },
    /** Receiving document permissions. */
    ReceivingDocument: {
        View: 'receivingdocument:view',
        Approve: 'receivingdocument:approve',
        Sync: 'receivingdocument:sync',
    },
    /** Purchase order permissions. */
    PurchaseOrder: {
        View: 'purchaseorder:view',
        Complete: 'purchaseorder:complete',
        Sync: 'purchaseorder:sync',
    },
    /** Customer agreement permissions. Reads on agreements are enforced (not open-by-default). */
    Agreement: {
        View: 'agreement:view',
        Create: 'agreement:create',
        Delete: 'agreement:delete',
        RecordAcceptance: 'agreement:recordacceptance',
        Update: 'agreement:update',
        Read: 'agreement:read',
    },
    /** Tenant branding permissions. */
    Branding: {
        View: 'branding:view',
        ClearCache: 'branding:clearcache',
        Delete: 'branding:delete',
        Update: 'branding:update',
        UploadLogo: 'branding:uploadlogo',
    },
    /** Time clock permissions. */
    TimeClock: {
        View: 'timeclock:view',
        Create: 'timeclock:create',
        Delete: 'timeclock:delete',
        Update: 'timeclock:update',
    },
    /** User account management permissions. */
    User: {
        View: 'user:view',
        AssignRoles: 'user:assignroles',
        Create: 'user:create',
        Delete: 'user:delete',
        ResetPassword: 'user:resetpassword',
        Sync: 'user:sync',
        Update: 'user:update',
        /** Provision a user account. Admin/system-only; not granted to any catalog-governed role. */
        Provision: 'user:provision',
    },
    /** User device registration permissions. A single management key gates the user-device read surface (device lookups and registration history), restoring the original Advisor-tier (tenantAdmin/storeManager/advisor) gating these endpoints carried before the permission migration. */
    UserDevice: {
        /** View and manage user device registrations and their registration history. */
        Manage: 'userdevice:manage',
    },
    /** Tenant mobile-device allowlist permissions. A single management key gates the per-tenant device allowlist surface: viewing the approved devices and adding or removing entries. Mirrors the UserDevice.Manage pattern -- one manage key gates the device read surface plus the add/remove actions. */
    TenantDevice: {
        /** View and manage the tenant mobile-device allowlist (view plus add/remove). */
        Manage: 'tenantdevice:manage',
    },
    /** Role management permissions. */
    Role: {
        View: 'role:view',
        Create: 'role:create',
        Delete: 'role:delete',
        Update: 'role:update',
        /** Manage tenant-level overrides of non-privileged system roles' default permission sets. Deliberately distinct from Update so a tenant can grant custom-role edit (per-role permission reconciliation on tenant-owned roles) without also handing over the power to redefine what a system role means inside their tenant. */
        ManageOverride: 'role:manage-override',
    },
    /** Tenant-level permissions. Read is an enforced read (not open-by-default); List is admin-only cross-tenant access. */
    Tenant: {
        Read: 'tenant:read',
        List: 'tenant:list',
        /** Create a tenant. Admin/system-only; not granted to any catalog-governed role. */
        Create: 'tenant:create',
        /** Update a tenant. Admin/system-only; not granted to any catalog-governed role. */
        Update: 'tenant:update',
        /** Change a tenant's SMS sender (texting) phone number. A finer-grained capability than Update so a custom role granted broad tenant-edit rights still cannot alter the messaging sender number. Admin/system-only; not granted to any catalog-governed role, so only the all-permissions bypass roles hold it. */
        UpdateTextingPhoneNumber: 'tenant:updatetextingphonenumber',
        /** Delete a tenant. Admin/system-only; not granted to any catalog-governed role. */
        Delete: 'tenant:delete',
        /** Synchronise a tenant. Admin/system-only; not granted to any catalog-governed role. */
        Sync: 'tenant:sync',
        /** Export a tenant's configuration bundle and import one into a tenant. Admin/system-only; not granted to any catalog-governed role. */
        ConfigTransfer: 'tenant:configtransfer',
    },
    /** Account type configuration permissions. */
    AccountType: {
        View: 'accounttype:view',
        Create: 'accounttype:create',
        Delete: 'accounttype:delete',
        Update: 'accounttype:update',
    },
    /** Address type configuration permissions. */
    AddressType: {
        View: 'addresstype:view',
        Sync: 'addresstype:sync',
    },
    /** External provider configuration permissions, scoped by branch, tenant, and user-branch. */
    ExternalProvider: {
        View: 'externalprovider:view',
        /** Resolves a single provider through the User+Branch > Branch > Tenant fallback chain, for the caller's own user id only. Held by operational roles that run integrations (a technician transferring a DVI to Tech Check) so they never need the catalog-wide View key, which also unlocks the tenant, branch, and user list reads. */
        Resolve: 'externalprovider:resolve',
        /** Branch-scoped external provider permissions. */
        Branch: {
            Create: 'externalprovider:branch:create',
            Delete: 'externalprovider:branch:delete',
            Update: 'externalprovider:branch:update',
        },
        /** Tenant-scoped external provider permissions. */
        Tenant: {
            Create: 'externalprovider:tenant:create',
            Delete: 'externalprovider:tenant:delete',
            Update: 'externalprovider:tenant:update',
        },
        /** User-branch-scoped external provider permissions. */
        UserBranch: {
            Create: 'externalprovider:userbranch:create',
            Delete: 'externalprovider:userbranch:delete',
            Update: 'externalprovider:userbranch:update',
        },
    },
    /** Feature flag permissions. Feature flag management is admin/system-only. */
    FeatureFlag: {
        View: 'featureflag:view',
        ClearCache: 'featureflag:clearcache',
        Toggle: 'featureflag:toggle',
    },
    /** File API permissions. The File API is otherwise open to any authenticated user with branch access; this key is the ownership-bypass gate that lets a designated tier overwrite or delete blobs uploaded by another user in the same tenant. */
    File: {
        /** Bypass object-level ownership on File uploads and deletes -- overwrite or delete a blob owned by another user within the same tenant. Granted to Tenant Admin by default; custom roles cloned from Tenant Admin inherit it via the permission catalog. */
        OverrideOwnership: 'file:overrideownership',
    },
    /** Notification permissions. */
    Notification: {
        View: 'notification:view',
        Send: 'notification:send',
    },
    /** Vehicle record permissions. */
    Vehicle: {
        View: 'vehicle:view',
        Update: 'vehicle:update',
        /** Synchronise vehicle records from the source system. */
        Sync: 'vehicle:sync',
    },
    /** System-level operational permissions. These keys are admin/system-only and are not granted to any catalog-governed role; access is via the admin/system bypass. */
    System: {
        /** Manage system caches (inspect, clear, or rebuild). */
        CacheManage: 'system:cachemanage',
    },
    /** Announcement management permissions. This key is admin/system-only and is not granted to any catalog-governed role; access is via the admin/system bypass. */
    Announcement: {
        /** Create, edit, and remove system announcements. */
        Manage: 'announcement:manage',
    },
    /** Tenant and branch operational settings permissions. A single manage key gates the whole settings surface, reads included, because the values are tenant-admin owned configuration rather than general operational data. Mirrors the TenantDevice pattern. */
    Settings: {
        /** View and change tenant and branch setting values. */
        Manage: 'settings:manage',
    },
    /** Translation override permissions. A single manage key gates the whole wording surface, reads included, because overrides are tenant-admin owned configuration. Mirrors the Settings pattern. */
    Translation: {
        /** View and change the tenant and branch wording overrides for UI labels. */
        Manage: 'translation:manage',
    },
    /** Audit log permissions. View exposes the audit trail to tenant administrators; catalog management is admin/system-only. */
    Audit: {
        /** View the audit trail. */
        View: 'audit:view',
        /** Audit catalog (event type) management permissions. Admin/system-only; not granted to any catalog-governed role. */
        Catalog: {
            /** Manage the audit event catalog. Admin/system-only; access is via the admin/system bypass. */
            Manage: 'audit:catalog:manage',
        },
    },
    /** Bulk data import permissions. View gates opening the Import area and is the minimum required to reach any import surface; individual import types additionally require their own domain permissions (all-of), enforced server-side against the resolved import definition's required-permission set. */
    Import: {
        /** Open the bulk Data Import area and list the available import types. */
        View: 'import:view',
    },
} as const;

/**
 * Recursively collects every string-valued leaf in a nested permission group into a flat array.
 *
 * @param group A permission group node (an object whose leaves are permission key strings).
 * @returns Every permission key string declared under the group, in declaration order.
 */
function collectPermissionKeys(group: Record<string, unknown>): string[] {
    const keys: string[] = [];
    for (const value of Object.values(group)) {
        if (typeof value === 'string') {
            keys.push(value);
        } else if (value && typeof value === 'object') {
            keys.push(...collectPermissionKeys(value as Record<string, unknown>));
        }
    }
    return keys;
}

/**
 * Recursively extracts the union of every string-valued leaf from a nested permission group type.
 */
type PermissionLeafValues<T> = T extends string
    ? T
    : T extends Record<string, unknown>
      ? { [K in keyof T]: PermissionLeafValues<T[K]> }[keyof T]
      : never;

/**
 * Union of every valid permission key string. Use this type for any parameter or field that holds
 * a permission key so the compiler rejects keys that are not part of the catalog.
 */
export type PermissionKey = PermissionLeafValues<typeof Permissions>;

/**
 * The distinct set of every permission key declared by {@link Permissions}, materialised once.
 * This is the front-end analogue of the backend `Permissions.All` collection and the oracle the
 * drift check compares against the backend registry and seed matrix.
 */
export const ALL_PERMISSIONS: readonly PermissionKey[] = Object.freeze([...new Set(collectPermissionKeys(Permissions) as PermissionKey[])]);
